<template>
  <div>
    <v-autocomplete ref="input"
                    v-model="valueMediator"
                    :label="label"
                    :search-input.sync="search"
                    :items="items"
                    :loading="loading"
                    :error-messages="message"
                    :auto-select-first="autoSelectFirst"
                    :readonly="readonly"
                    :disabled="disabled"
                    item-value="place_id"
                    item-text="title"
                    no-filter
                    return-object
                    :clearable="clearable"
                    @focus="focused = true"
                    @blur="focused = false"
                    @keydown="focused = true"
    >
      <template v-slot:append-outer>
        <slot name="action" />
      </template>
      <template v-slot:item="data">
        <v-list-item-icon>
          <v-icon>{{ icon(data.item.type) }}</v-icon>
        </v-list-item-icon>

        <v-list-item-content>
          <v-list-item-title>
            {{ data.item.title }}
          </v-list-item-title>
          <v-list-item-subtitle>
            <span v-if="data.item.subtitle">{{ data.item.subtitle }}</span>
          </v-list-item-subtitle>
        </v-list-item-content>
      </template>
    </v-autocomplete>
    <v-card ref="mapHolder"
            class="pa-0 overflow-hidden rounded-lg"
            color="transparent"
            @mouseover="hover = true"
            @mouseleave="hover = false"
    >
      <div ref="map" style="width:100%;height:350px;" />
      <v-menu ref="mapMenu"
              v-model="mapMenuActive"
              :nudge-left="-mapMenuX"
              :nudge-top="-mapMenuY"
              :attach="$refs.mapHolder ? $refs.mapHolder.$el : null"
              min-width="200"
              max-width="300"
              max-height="150"
              :close-on-click="false"
              :close-on-content-click="false"
              class="overflow-visible"
              style="contain: size !important;"
              content-class="rounded-0 rounded-lg rounded-tl-0 overflow-hidden"
              absolute
      >
        <v-btn absolute small icon style="right:0; top:0; z-index: 5" @click="mapMenuClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-card :loading="mapMenuLoading" class="overflow-visible rounded-0" loader-height="50">
          <v-card-text v-if="mapMenuData" class="mr-8 pb-0">
            {{ mapMenuData.name }}
          </v-card-text>
          <v-card-actions>
            <v-btn v-if="mapMenuData.street_number" class="flex-grow-1" @click="mapMenuApply">
              <v-icon left>
                mdi-map-marker
              </v-icon> Выбрать
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'AddressInput',
  props: {
    value: { type: Object, default: null },
    label: { type: String, default: null },
    readonly: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    autoSelectFirst: { type: Boolean, default: false },
    // props about map
    // areas: { type: Array, default: () => [] },
    area: { type: Array, default: () => [] },
    showAreas: { type: Boolean, default: true },
  },
  data: () => ({
    itemsFound: [],
    timer: null,
    loading: false,
    search: null,
    message: null,
    map: null,
    marker: null,
    autocompleteService: null,
    placesService: null,
    bounds: null,
    pickOnMap: false,
    hover: false,
    focused: false,
    mapMenuData: {},
    mapMenuTimer: null,
    mapMenuActive: false,
    mapMenuX: 0,
    mapMenuY: 0,
    mapMenuLng: 0,
    mapMenuLat: 0,
    zooming: false,
  }),
  computed: {
    valueMediator: {
      get() {
        return this.value;
      },
      set(prediction) {
        if (prediction && prediction.place_id) {
          const request = {
            placeId: prediction.place_id,
            fields: ['address_components', 'formatted_address', 'geometry.location', 'types', 'name']
          };
          this.placesService.getDetails(request, (details) => this.applyPlace(details, prediction));
        } else {
          this.$emit('input', null);
        }
      }
    },
    items() {
      if (this.value && this.value.place_id) {
        const out = [...(this.itemsFound || [])];
        const { place_id } = this.value;
        const index = out.findIndex((row) => row.place_id === place_id);
        if (index === -1) {
          out.push(this.value);
        }
        return out;
      }
      return this.itemsFound || [];
    },
    mapMenuLoading() {
      if (this.mapMenuData) {
        return this.mapMenuData.loading;
      }
      return this.mapMenuActive;
    }
  },
  watch: {
    value() {
      this.updateMarker(this.value ? this.value.location : null);
    },
    search(search) {
      if (!this.focused) return;
      if (search && search.toString().length >= 1) {
        clearTimeout(this.timer);
        this.timer = setTimeout(this.searchStart, 200);
      } else {
        this.loading = false;
      }
    }
  },
  mounted() {
    this.map = new google.maps.Map(this.$refs.map, {
      zoom: 12,
      center: { lat: 55.7554345, lng: 37.6192113 },
      fullscreenControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      clickableIcons: false,
    });

    this.placesService = new google.maps.places.PlacesService(this.map);
    this.autocompleteService = new google.maps.places.AutocompleteService();

    if (!this.value) {
      this.getLocation();
    }

    this.bounds = this.$gmaps.getBounds(this.area);
    if (this.bounds) {
      this.map.fitBounds(this.bounds);
    }

    if (this.showAreas) {
      this.$gmaps.drawHoleArea(this.map, this.area);
    }

    this.map.addListener('click', this.clickOnMap);
    this.map.addListener('bounds_changed', this.mapMenuRepositionOnBounds);
    this.map.addListener('zoom_changed', this.mapMenuRepositionOnZoom);
  },
  methods: {
    mapMenuRepositionOnBounds() {
      this.zooming = false;
      this.mapMenuReposition();
    },
    mapMenuRepositionOnZoom() {
      this.zooming = true;
      this.mapMenuReposition();
    },
    mapMenuReposition() {
      const latLng = new google.maps.LatLng(this.mapMenuLat, this.mapMenuLng);
      const pnt = this.$gmaps.latLng2Point(latLng, this.map);
      this.mapMenuX = pnt.x;
      this.mapMenuY = pnt.y;
    },
    applyPlace(details, prediction) {
      this.message = null;
      const street_number = this.extractStreetNumber(details.address_components);
      if (!street_number) {
        this.askStreetNumber(details.name, details.geometry.location);
      } else {
        const input = {
          text: details.formatted_address,
          location: {
            lat: details.geometry.location.lat(),
            lng: details.geometry.location.lng(),
          },
          title: details.name,
          street_number,
          subtitle: prediction && prediction.subtitle ? prediction.subtitle : null,
          place_id: prediction && prediction.place_id ? prediction.place_id : details.place_id
        };
        this.search = details.name;
        this.$emit('input', input);
      }
    },
    extractStreetNumber(components) {
      if (!components || !Array.isArray(components) || components.length === 0) return false;
      const { types, long_name } = components[0];
      if (!types || !Array.isArray(types) || types.length === 0) return false;
      if (types[0] === 'street_number' || types[0] === 'premise') {
        return long_name;
      }
      return null;
    },
    askStreetNumber(search, position) {
      this.focus();
      setTimeout(() => {
        this.search = `${search}, `;
        this.message = this.$dictionary.get('geo-specify-house');
      }, 100);
      this.updateMarker(position);
    },
    focus() {
      this.$refs.input.focus();
    },
    searchStart() {
      console.log('start search');
      if (this.search) {
        const request = { input: this.search /* , types: ['address'] */ };
        if (this.bounds) {
          request.bounds = this.bounds;
        }
        this.loading = true;
        this.autocompleteService.getPlacePredictions(request, this.searchFinish);
      }
    },
    searchFinish(places, status) {
      this.loading = false;
      this.itemsFound = [];
      if (status !== google.maps.places.PlacesServiceStatus.OK) return;
      for (let i = 0; i < places.length; i++) {
        const place = places[i];
        this.itemsFound.push({
          place_id: place.place_id,
          title: place.structured_formatting.main_text,
          subtitle: place.structured_formatting.secondary_text,
          description: place.description,
          type: Array.isArray(place.types) ? place.types[0] : place.types
          // full: place,
        });
      }
      this.loading = false;
    },
    updateMarker(position) {
      if (!position) {
        if (this.marker) this.marker.setMap(null);
        return;
      }
      if (!this.marker) {
        this.marker = new google.maps.Marker({ map: this.map, position });
      } else {
        this.marker.setMap(this.map);
        this.marker.setPosition(position);
      }
      this.map.setCenter(position);
    },
    applyCoordinates(location) {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location }, (locations) => {
        if (locations) {
          const data = locations[0];
          data.name = data.formatted_address;
          const street_number = this.extractStreetNumber(data.address_components);
          if (data.name && street_number) {
            this.applyPlace(data);
          }
        }
      });
    },
    mapMenuUpdate(location) {
      this.mapMenuActive = true;
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location }, (locations, status) => {
        if (status !== 'OK') console.log(status);
        if (locations) {
          const data = locations[0];
          data.name = data.formatted_address;
          if (data.name) {
            data.street_number = this.extractStreetNumber(data.address_components);
            this.mapMenuData = data;
            this.mapMenuActive = true;
            this.mapMenuLat = data.geometry.location.lat();
            this.mapMenuLng = data.geometry.location.lng();
            this.mapMenuReposition();
          } else {
            this.mapMenuActive = false;
          }
        } else {
          console.log('nothing!');
          this.mapMenuActive = false;
        }
      });
    },
    clickOnMap(e) {
      let ok = true;
      if (this.area && Array.isArray(this.area) && this.area.length > 0) {
        const poly = new google.maps.Polygon({ paths: this.area });
        ok = google.maps.geometry.poly.containsLocation(e.latLng, poly);
      }
      this.mapMenuActive = true;
      this.mapMenuX = e.pixel.x;
      this.mapMenuY = e.pixel.y;
      this.mapMenuLat = e.latLng.lat();
      this.mapMenuLng = e.latLng.lng();
      clearTimeout(this.mapMenuTimer);
      if (ok) {
        this.mapMenuData = { loading: true };
        this.mapMenuTimer = setTimeout(() => this.mapMenuUpdate(e.latLng), 1000);
      } else {
        this.mapMenuData = { name: 'Эта область не поддеживается' };
      }
    },
    getLocation() {
      navigator.geolocation.getCurrentPosition((position) => {
        this.applyCoordinates({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      }, (error) => {
        console.log(error);
      });
    },
    icon(type) {
      if (!type) return 'mdi-check-bold';
      return this.$geoIcons.get(type);
    },
    mapMenuClose() {
      clearTimeout(this.mapMenuTimer);
      this.mapMenuActive = false;
    },
    mapMenuApply() {
      if (this.mapMenuData.place_id) {
        this.applyPlace(this.mapMenuData);
      }
      this.mapMenuClose();
    }
  }
};
</script>
